import React, {
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";

import { InternetConnectionBar } from "./InternetConnectionBar";
import { NoAssignedDashboard } from "../NoAssignedDashboard";
import { useRegisterAliveDevice } from "../../OS/auth/registerAlive";
import { graphql, useLazyLoadQuery, useSubscription } from "react-relay";
import { Storecast } from "./Storecast";
import { StorecastScreenQuery } from "./__generated__/StorecastScreenQuery.graphql";
import { authService } from "../../OS/auth/authService";

import { StorecastFocus } from "../Focus";
import { StorecastScreenDashboardChangedSubscription } from "./__generated__/StorecastScreenDashboardChangedSubscription.graphql";
import { NoName } from "../NoName";
import { ScreenScaled } from "../../common/ScreenScaled";
import { Info } from "../Info";
import { useTime } from "arkh";
import { DateTime } from "luxon";
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from "@sentry/react-native";
import { FocusProvider } from "widgets/src/useSetFocus";
import { SubscriptionStatus } from "@/src/subscriptionStatus";
import { useInterval } from "widgets/useInterval";

export const StorecastScreen = (props: {
  subscriptionStatus: SubscriptionStatus;
}) => {
  const isReconnecting =
    props.subscriptionStatus === SubscriptionStatus.reconnecting;

  const [reconnectKey, setReconnectKey] = useState(0);
  useEffect(() => {
    if (isReconnecting) {
      return () => {
        setReconnectKey((prev) => prev + 1);
      };
    }
  }, [isReconnecting]);
  const [_pollKey, setPollKey] = useState(0);
  const pollKey = useDeferredValue(_pollKey);
  const [hasFocus, setHasFocus] = useState(false);
  const updatePollKey = useCallback(() => {
    if (!hasFocus) {
      setPollKey((prev) => prev + 1);
    }
  }, [hasFocus]);
  useInterval(updatePollKey, 120000);
  const [_currentDashboardId, _setCurrentDashboardId] = useState<string | null>(
    null
  );
  const currentDashboardId = useDeferredValue(_currentDashboardId);

  const data = useLazyLoadQuery<StorecastScreenQuery>(
    graphql`
      query StorecastScreenQuery($dashboardId: ID) {
        storecast {
          screen {
            id
            createdAt
            name
            showInfo
            shouldAnalyze

            inputMode
            dashboard(dashboardId: $dashboardId) {
              id
              __typename
            }
            remoteSelectedDashboard: dashboard {
              id
            }
            ...Storecast_storecastScreen
            ...InfoBoxScreen_StorecastScreen
          }
        }
      }
    `,

    { dashboardId: currentDashboardId },
    {
      fetchPolicy: "store-and-network",
      fetchKey: reconnectKey + ":" + pollKey,
    }
  );
  const screenId = data.storecast.screen.id;

  const remoteSelectedDashboardId =
    data.storecast.screen.remoteSelectedDashboard?.id;

  useEffect(() => {
    // if the remote dashboard id is changed, we reset the selected current dashboard
    if (remoteSelectedDashboardId) {
      _setCurrentDashboardId(null);
    }
  }, [remoteSelectedDashboardId, _setCurrentDashboardId]);

  useEffect(() => {
    Sentry.setUser({
      id: screenId,
      username: data.storecast.screen.name,
    });
  }, [screenId, data.storecast.screen.name]);

  useRegisterAliveDevice(screenId);

  useSubscription<StorecastScreenDashboardChangedSubscription>(
    useMemo(
      () => ({
        onNext(response) {
          if (response?.storecastScreenChange === null) {
            authService.send("STORECAST_SCREEN_DEVICE_UNDEFINED");
          }
        },
        variables: { screenId, dashboardId: currentDashboardId },
        subscription: graphql`
          subscription StorecastScreenDashboardChangedSubscription(
            $screenId: ID!
            $dashboardId: ID
          ) {
            storecastScreenChange(screenId: $screenId) {
              id
              name
              showInfo
              inputMode

              ...Storecast_storecastScreen
            }
          }
        `,
      }),
      [currentDashboardId, screenId]
    )
  );

  const storecastCreatedAt = useMemo(() => {
    return DateTime.fromISO(data.storecast.screen.createdAt);
  }, [data.storecast.screen.createdAt]);

  const time = useTime(30000);

  const touchMode = data.storecast.screen.inputMode === "TOUCH";
  const [localFocus, setLocalFocus] = useState<{
    widgetId: string;
    detailItemId: string | undefined;
  } | null>(null);

  const onFocus = useCallback((widgetId: string, detailItemId?: string) => {
    setLocalFocus({
      widgetId,
      detailItemId: detailItemId,
    });
  }, []);

  const clearManualFocus = useCallback(() => {
    setLocalFocus(null);
  }, []);

  if (!data?.storecast.screen.name) {
    return (
      <ScreenScaled>
        <NoName />
      </ScreenScaled>
    );
  }

  if (
    data?.storecast.screen.showInfo &&
    time.diff(storecastCreatedAt).minutes < 5
  ) {
    return (
      <ScreenScaled>
        <Info
          screen={data.storecast.screen}
          screenName={data.storecast.screen.name}
        />
      </ScreenScaled>
    );
  }

  if (!data?.storecast.screen?.dashboard) {
    return (
      <ScreenScaled>
        <NoAssignedDashboard
          screenName={data?.storecast.screen?.name ?? undefined}
        />
      </ScreenScaled>
    );
  }

  return (
    <>
      <InternetConnectionBar subscriptionStatus={props.subscriptionStatus} />
      <FocusProvider focus={onFocus}>
        <Storecast
          storecast={data.storecast.screen}
          touchMode={touchMode}
          onSelectDashboard={_setCurrentDashboardId}
          fetchKey={reconnectKey + ""}
          pollFetchKey={pollKey + ""}
        />
      </FocusProvider>
      <ErrorBoundary
        fallbackRender={() => null}
        onError={(e) => {
          Sentry.captureException(e);
        }}
      >
        <Suspense fallback={null}>
          <StorecastFocus
            key={data.storecast.screen.id}
            onHasFocusChanged={setHasFocus}
            storecastId={data.storecast.screen.dashboard.id}
            shouldAnalyze={data.storecast.screen.shouldAnalyze}
            touchMode={touchMode}
            manualFocus={localFocus ?? undefined}
            clearManualFocus={clearManualFocus}
          />
        </Suspense>
      </ErrorBoundary>
    </>
  );
};
